import { FC, memo, useCallback } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useSortBy } from 'react-instantsearch';
import { useEvents } from '~/shared/hooks/useEvents';
import { useDeferredFunction } from '~/shared/hooks/useDeferredFunction';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import {
    StyledContainer,
    StyledTrigger,
    StyledChevron,
    StyledDropdown,
    StyledOption,
    StyledCheckmark,
} from './SortBy.styled';
import { AlgoliaSortOption, algoliaSortOptions as fallbackOptions } from '~/services/instantsearch';

const SortBy: FC = () => {
    const { data: frameData } = useFrame();
    const { translate } = useTranslation();
    const { filterEvent } = useEvents(frameData);

    // TODO(IMC-2274): Remove fallback hardcoded options once configuration is available in all environments.
    const { currentRefinement, options, refine } = useSortBy({
        items: frameData.productSettings?.sortingMethods?.length
            ? frameData.productSettings.sortingMethods
            : fallbackOptions,
    });

    const deferredRefine = useDeferredFunction(refine);

    const selectedOption =
        options.find((option) => option.value === currentRefinement) ?? options[0] ?? null;

    const selectedLabel = selectedOption?.label ?? '';

    const handleChange = useCallback(
        (selected) => {
            deferredRefine(selected.value);
            filterEvent('Sorting', selected.label);
        },
        [deferredRefine, filterEvent],
    );

    return (
        <StyledContainer defaultOpen={false} modal={false}>
            <StyledTrigger aria-label={`Sorter efter: ${selectedLabel}`}>
                {selectedLabel}
                <StyledChevron />
            </StyledTrigger>
            <DropdownMenu.Portal>
                <StyledDropdown
                    align="end"
                    sideOffset={20}
                    avoidCollisions={false}
                    collisionPadding={0}
                    forceMount
                >
                    <DropdownMenu.RadioGroup value={currentRefinement}>
                        {options.map((option) => {
                            const itemTyped = option as AlgoliaSortOption;

                            /**
                             * Items starting with 'plp.' are dictionary keys and should not be used directly without using translate().
                             *
                             * TODO(IMC-2274): Remove fallback hardcoded options once configuration is available in all environments.
                             */
                            const label = itemTyped.label.startsWith('plp.')
                                ? translate(itemTyped.label)
                                : itemTyped.label;
                            return (
                                <StyledOption
                                    key={option.value}
                                    value={option.value}
                                    onSelect={() => handleChange(option)}
                                >
                                    <span>{label}</span>
                                    <DropdownMenu.ItemIndicator>
                                        <StyledCheckmark />
                                    </DropdownMenu.ItemIndicator>
                                </StyledOption>
                            );
                        })}
                    </DropdownMenu.RadioGroup>
                </StyledDropdown>
            </DropdownMenu.Portal>
        </StyledContainer>
    );
};

export default memo(SortBy);
